// Playback controller: bridges the store and the audio engine (§4.11), runs
// the playhead animation loop and the play-on-select/input previews.

import { tickOfPosition, beatOnset, selectionRange, playedBarAt, type Timeline } from '@simplenotation/core';
import { getState, setState, useStore, setPlaybackPrefs, onEditorFeedback, toast } from '../state/store';
import { getAudioEngine, type AudioEngine } from '../audio/engine';
import { track } from '../analytics';

let engine: AudioEngine | null = null;
let raf = 0;

/** The engine must be created/resumed synchronously inside a user gesture: no awaits before play(). */
function getEngine(): AudioEngine {
  if (engine) return engine;
  const e = getAudioEngine();
  engine = e;
  syncEngine(e);
  e.on('state', (st) => {
    setState((s) => ({ playback: { ...s.playback, playing: st.playing, countingIn: st.countingIn } }));
    if (st.playing) startLoop();
    else {
      cancelAnimationFrame(raf);
      setState((s) => ({ playback: { ...s.playback, tick: e.currentTick() } }));
    }
  });
  e.on('ended', () => setState((s) => ({ playback: { ...s.playback, playing: false } })));
  e.on('countin', (c) => setState((s) => ({ playback: { ...s.playback, countingIn: c.active } })));
  return e;
}

function syncEngine(e: AudioEngine): void {
  const s = getState();
  e.setScore(s.ed.score, s.timeline);
  e.setSpeed(s.playbackPrefs.speed);
  e.setMetronome(s.playbackPrefs.metronome);
  e.setCountIn(s.playbackPrefs.countIn);
  e.setLatencyMs(s.view.latencyMs);
  e.setMasterVolume(s.view.volume);
  e.setLoop(s.playback.loop ? { startTick: s.playback.loop.startTick, endTick: s.playback.loop.endTick } : null);
}

// Keep the engine in sync with the store.
let lastTimeline: Timeline | null = null;
useStore.subscribe((s, prev) => {
  if (!engine) return;
  if (s.timeline !== lastTimeline) {
    lastTimeline = s.timeline;
    engine.setScore(s.ed.score, s.timeline);
    // Loop ticks follow bar positions after edits.
    if (s.playback.loop) {
      const { fromBar, toBar } = s.playback.loop;
      const loop = loopFor(s.timeline, fromBar, toBar);
      if (loop) {
        setState((st) => ({ playback: { ...st.playback, loop: { ...loop, fromBar, toBar } } }));
        engine.setLoop(loop);
      }
    }
  }
  if (s.playbackPrefs !== prev.playbackPrefs) {
    engine.setSpeed(s.playbackPrefs.speed);
    engine.setMetronome(s.playbackPrefs.metronome);
    engine.setCountIn(s.playbackPrefs.countIn);
  }
  if (s.view.latencyMs !== prev.view.latencyMs) engine.setLatencyMs(s.view.latencyMs);
  if (s.view.volume !== prev.view.volume) engine.setMasterVolume(s.view.volume);
});

function startLoop(): void {
  cancelAnimationFrame(raf);
  const step = () => {
    if (!engine) return;
    const tick = engine.currentTick();
    setState((s) => (Math.abs(s.playback.tick - tick) < 0.5 ? s : { playback: { ...s.playback, tick } }));
    if (engine.playing) raf = requestAnimationFrame(step);
  };
  raf = requestAnimationFrame(step);
}

/** Played tick of the cursor position (first occurrence). */
export function cursorTick(): number {
  const s = getState();
  const c = s.ed.cursor;
  return tickOfPosition(s.timeline, c.bar, beatOnset(s.ed.score, c));
}

/** Only the first playback per page load is an event; every pause/resume would drown the rest. */
let playTracked = false;

export async function play(fromTick?: number): Promise<void> {
  const e = getEngine();
  const s = getState();
  let start = fromTick ?? cursorTick();
  if (s.playback.loop && (start < s.playback.loop.startTick || start >= s.playback.loop.endTick)) start = s.playback.loop.startTick;
  try {
    await e.play(start);
    // Paused or stopped while the sounds loaded: the engine never started.
    if (!e.playing) return;
    setState((st) => ({ playback: { ...st.playback, playing: true, tick: start } }));
    startLoop();
    if (!playTracked) {
      playTracked = true;
      track('play_score');
    }
  } catch (err) {
    toast(`Audio couldn’t start: ${(err as Error).message}`, 'error');
  }
}

export function pause(): void {
  engine?.pause();
  cancelAnimationFrame(raf);
  setState((s) => ({ playback: { ...s.playback, playing: false } }));
}

export async function togglePlay(): Promise<void> {
  if (getState().playback.playing) pause();
  else await play();
}

/** Enter: seek to the start (of the loop, if any). */
export function seekToStart(): void {
  const s = getState();
  const t = s.playback.loop?.startTick ?? 0;
  setState((st) => ({ playback: { ...st.playback, tick: t } }));
  if (engine) {
    if (engine.playing) engine.seek(t);
  }
}

/** Seek to a played tick (click on the progress bar). */
export function seek(tick: number): void {
  setState((st) => ({ playback: { ...st.playback, tick } }));
  if (engine?.playing) engine.seek(tick);
}

function loopFor(tl: Timeline, fromBar: number, toBar: number): { startTick: number; endTick: number } | null {
  const start = tl.bars.find((b) => b.barIdx === fromBar);
  if (!start) return null;
  const startIdx = tl.bars.indexOf(start);
  let endIdx = startIdx;
  for (let i = startIdx; i < tl.bars.length; i++) {
    if (tl.bars[i].barIdx === toBar) {
      endIdx = i;
      break;
    }
  }
  const end = tl.bars[endIdx];
  return { startTick: start.startTick, endTick: end.startTick + end.length };
}

/** Loop the selected bars (or the cursor bar); toggles off when already looping. */
export function toggleLoop(): void {
  const s = getState();
  if (s.playback.loop) {
    setState((st) => ({ playback: { ...st.playback, loop: null } }));
    engine?.setLoop(null);
    return;
  }
  const r = selectionRange(s.ed);
  setLoopBars(r.start.bar, r.end.bar);
}

export function setLoopBars(fromBar: number, toBar: number): void {
  const s = getState();
  const a = Math.min(fromBar, toBar);
  const b = Math.max(fromBar, toBar);
  const loop = loopFor(s.timeline, a, b);
  if (!loop) return;
  setState((st) => ({ playback: { ...st.playback, loop: { ...loop, fromBar: a, toBar: b } } }));
  engine?.setLoop(loop);
}

export function clearLoop(): void {
  setState((st) => ({ playback: { ...st.playback, loop: null } }));
  engine?.setLoop(null);
}

export function toggleMetronome(): void {
  const m = getState().playbackPrefs.metronome;
  setPlaybackPrefs({ metronome: { ...m, enabled: !m.enabled } });
}

export function setSpeed(speed: number): void {
  setPlaybackPrefs({ speed: Math.max(0.25, Math.min(2, Math.round(speed * 100) / 100)) });
}

export function nudgeSpeed(delta: number): void {
  setSpeed(getState().playbackPrefs.speed + delta);
}

/** Bar index (score) currently under the playhead. */
export function playheadBar(): number {
  const s = getState();
  const i = playedBarAt(s.timeline, s.playback.tick);
  return s.timeline.bars[i]?.barIdx ?? 0;
}

// Play-on-select / play-on-input previews.
onEditorFeedback((ed, kind) => {
  const s = getState();
  if (s.playback.playing) return;
  const pref = kind === 'input' ? s.editorPrefs.playOnInput : s.editorPrefs.playOnSelect;
  if (pref === 'none') return;
  const c = ed.cursor;
  const beat = ed.score.tracks[c.track]?.bars[c.bar]?.voices[c.voice]?.[c.beat];
  if (!beat || beat.rest || !beat.notes.length) return;
  const track = ed.score.tracks[c.track];
  let notes = beat.notes;
  if (pref === 'note') {
    const lane = c.lane;
    const hit = lane === 'tab' ? notes.find((n) => n.string === c.string) : undefined;
    notes = [hit ?? notes[notes.length - 1]];
  }
  const midis = notes.map((n) => {
    const SEMI: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
    return 12 * (n.pitch.octave + 1) + SEMI[n.pitch.step] + n.pitch.alter + track.writtenToSounding;
  });
  void getEngine().previewNotes(midis, track.sound);
});

export { getEngine };
